import { message } from 'antd';
import {
  AnalyzingState,
  ChangeProductModal,
  LeaveConfirmModal,
  QuickOrderTextArea,
  ResultBanner,
  ResultFooter,
  ResultTable,
  messages as componentMessages,
} from 'components/QuickOrder';
import { BackIcon, ToastIcon } from 'components/QuickOrder/icons';
import { push } from 'connected-react-router';
import { refreshCart } from 'containers/MainLayout/actions';
import useMutationSaga, { GMutation } from 'hooks/useMutationSaga';
import { GQuery, useLazyQuerySaga } from 'hooks/useQuerySaga';
import React, { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import styled from 'styles/styled-components';
import translations from 'translations';
import { MutationCartPutItemLightsArgs, QueryAnalyzeSmartCartArgs } from 'types/schema';
import media from 'utils/mediaStyle';
import { analyzeSmartCart, cartPutItemLights } from 'utils/query';
import { useAnalyze } from './hooks/useAnalyze';
import { useAnalyzeResult } from './hooks/useAnalyzeResult';
import { useImageUpload } from './hooks/useImageUpload';
import messages from './messages';
import { mapItemsToProducts } from './utils';

const MainContainer = styled.div<{ hasFooter?: boolean }>`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 75pt);
  margin-top: 99.66px;
  padding-bottom: ${({ hasFooter }) => (hasFooter ? '144px' : '0')};

  ${media.md`
    margin-top: 105.66px;
  `};
`;

const BackContainer = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  margin: 24px 0;
  gap: 8px;
`;

const Toast = styled.div<{ visible: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px;
  height: 56px;
  background: #ffffff;
  border: 1px solid #f2f3f5;
  border-radius: 8px;
  box-shadow: 2px 6px 32px rgba(0, 0, 0, 0.1);
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #111826;
  pointer-events: none;
  white-space: nowrap;
  box-sizing: border-box;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transform: ${({ visible }) => (visible ? 'translateY(0)' : 'translateY(-6px)')};
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
`;

const BackIconWrapper = styled.a`
  background: none;
  border: none;
  padding: 8px 0 0;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
`;

const PageTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PageTitle = styled.h1`
  font-size: 30px;
  font-weight: 600;
  line-height: 40px;
  color: #111827;
  margin: 0;
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
  color: #111827;
  margin: 0;
`;

const NewBadge = styled.div`
  display: flex;
  height: 20px;
  padding: 0 8px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 80px;
  color: #fff;
  background: #0d66f6;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 40px 0;
  box-shadow: 0 4px 24px 0 rgba(58, 85, 9, 0.04);
  min-height: 500px;
`;

const ResultLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 768px;
  width: 100%;
  margin: 0 auto;
  padding: 0;
`;

const AnalyzeButton = styled.button<{ hasContent: boolean }>`
  font-family: 'Maven Pro';
  display: block;
  margin: 20px auto 0;
  padding: 10px 32px;
  border-radius: 6px;
  border: none;
  font-size: 15px;
  font-weight: 600;
  cursor: ${({ hasContent }) => (hasContent ? 'pointer' : 'not-allowed')};
  background: ${({ hasContent }) => (hasContent ? '#19a54a' : '#d9d9d9')};
  color: #fff;
  transition: background 0.2s;

  &:hover {
    background: ${({ hasContent }) => (hasContent ? '#158f3e' : '#d9d9d9')};
  }
`;

interface IDispatchProps {
  goToSignIn: () => void;
  goHome: () => void;
  gotoSignIn: () => void;
  onRefreshCart: () => void;
}

type QuickOrderProps = IDispatchProps;

const QuickOrder = ({ goToSignIn, goHome, gotoSignIn, onRefreshCart }: QuickOrderProps) => {
  const [text, setText] = useState('');
  const { images, fileError, addFiles, removeImage, clearImages, uploadAll } = useImageUpload();

  const fileErrorMessage =
    fileError === 'invalidFormat'
      ? translations(componentMessages.errorInvalidFormat)
      : fileError === 'tooLarge'
        ? translations(componentMessages.errorFileTooLarge)
        : fileError === 'tooMany'
          ? translations(componentMessages.errorTooManyFiles)
          : null;
  const { isAnalyzing, isDone, progress, start, finish, reset: resetAnalyze } = useAnalyze();
  const [analyze] = useLazyQuerySaga<GQuery<'analyzeSmartCart'>, QueryAnalyzeSmartCartArgs>(analyzeSmartCart, {
    // TODO: check
    authentication: false,
  });
  const [addItemsToCart, { loading: addingToCart }] = useMutationSaga<
    GMutation<'cartPutItemLights'>,
    MutationCartPutItemLightsArgs
  >(cartPutItemLights);
  const {
    products,
    quantities,
    allSelected,
    someSelected,
    hasHigh,
    selectedCount,
    totalAmount,
    found,
    toggleRow,
    toggleAll,
    selectAllHigh,
    setQuantity,
    swapProduct,
    reset: resetResult,
  } = useAnalyzeResult();

  const [changeProductTarget, setChangeProductTarget] = useState<{ id: string; anchor: HTMLElement } | null>(null);

  const [leaveConfirmAction, setLeaveConfirmAction] = useState<'home' | 'reanalyze' | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showToast = useCallback((msg?: string) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    if (msg) setToastMessage(msg);
    setToastVisible(true);
    toastTimerRef.current = setTimeout(() => setToastVisible(false), 3000);
  }, []);

  useEffect(
    () => () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    },
    [],
  );

  const loggedIn = !!localStorage.getItem('token');

  const isTextOverLimit = text.length > 1000;
  const hasContent = (text.trim().length || images.length) && !isTextOverLimit;

  const handleAnalyze = useCallback(async () => {
    if (!loggedIn) {
      gotoSignIn();
      return;
    }
    if (!hasContent) return;
    start();
    try {
      const filePaths = await uploadAll();
      const inquiryString = text.trim();
      const storeId = localStorage.getItem('store') ?? undefined;
      const data = await analyze({
        variables: {
          input: {
            ...(filePaths.length ? { filePaths: filePaths } : {}),
            ...(inquiryString ? { inquiryString: inquiryString } : {}),
            ...(storeId ? { storeId: storeId } : {}),
          },
        },
      });
      resetResult(mapItemsToProducts(data?.items ?? []));
      finish();
    } catch (e: any) {
      const errorMessage = Array.isArray(e) ? e[0]?.message : e?.message;
      if (errorMessage) message.error(errorMessage);
      resetAnalyze();
    }
  }, [hasContent, start, finish, resetAnalyze, uploadAll, analyze, text, resetResult]);

  const handleReanalyze = useCallback(() => {
    resetAnalyze();
    setText('');
    clearImages();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [resetAnalyze, clearImages]);

  const handleSelectAllHigh = useCallback(() => {
    selectAllHigh();
    showToast(translations(componentMessages.selectAllHighConfidenceSuccess));
  }, [selectAllHigh, showToast]);

  const hasNonNoneProduct = products.some((p) => p.confidence?.toLowerCase() !== 'none');

  const handleBackClick = useCallback(
    (e: MouseEvent) => {
      if (isDone && hasNonNoneProduct) {
        e.preventDefault();
        setLeaveConfirmAction('home');
      }
    },
    [isDone, hasNonNoneProduct],
  );

  const handleAddToCart = useCallback(async () => {
    if (addingToCart) return;
    const storeId = localStorage.getItem('store');
    if (!storeId) return;
    const items = products.reduce<{ productId: string; quantity: number }[]>((acc, p) => {
      if (p.selected && p.matchedProduct) {
        acc.push({
          productId: p.matchedProduct.productId,
          quantity: quantities[p.id] ?? p.defaultQuantity,
        });
      }
      return acc;
    }, []);
    if (!items.length) return;
    try {
      const response = await addItemsToCart({
        variables: { input: { storeId: storeId, items: items } },
      });
      if (response?.userErrors?.length) {
        message.error(response.userErrors[0].message);
        return;
      }
      onRefreshCart();
      showToast(translations(componentMessages.addToCartSuccessfully));
      setTimeout(handleReanalyze, 500);
    } catch (e: any) {
      const errorMessage = Array.isArray(e) ? e[0]?.message : e?.message;
      if (errorMessage) message.error(errorMessage);
    }
  }, [addingToCart, products, quantities, addItemsToCart, onRefreshCart, handleReanalyze]);

  const handleChangeProduct = useCallback((id: string, anchor: HTMLElement) => {
    setChangeProductTarget({ id: id, anchor: anchor });
  }, []);

  const handleSwapProduct = useCallback(
    (...args: Parameters<typeof swapProduct>) => {
      swapProduct(...args);
      showToast(translations(componentMessages.changeProductSuccessfully));
    },
    [swapProduct, showToast],
  );

  const changeProductItem = changeProductTarget
    ? (products.find((p) => p.id === changeProductTarget.id) ?? null)
    : null;

  return (
    <>
      {changeProductItem && changeProductTarget && (
        <ChangeProductModal
          product={changeProductItem}
          anchorEl={changeProductTarget.anchor}
          onSelect={handleSwapProduct}
          onClose={() => setChangeProductTarget(null)}
        />
      )}
      <Helmet>
        <title>{translations(messages.pageTitle)}</title>
      </Helmet>
      {leaveConfirmAction && (
        <LeaveConfirmModal
          title={
            leaveConfirmAction === 'reanalyze'
              ? translations(messages.leaveReanalyzeTitle)
              : translations(messages.leaveConfirmTitle)
          }
          body={
            leaveConfirmAction === 'reanalyze'
              ? translations(messages.leaveReanalyzeBody).replace('{count}', String(products.length))
              : translations(messages.leaveConfirmBody).replace('{count}', String(products.length))
          }
          confirmLabel={
            leaveConfirmAction === 'reanalyze'
              ? translations(messages.leaveConfirmReanalyze)
              : translations(messages.leaveConfirmOk)
          }
          onCancel={() => setLeaveConfirmAction(null)}
          onConfirm={() => {
            setLeaveConfirmAction(null);
            if (leaveConfirmAction === 'reanalyze') handleReanalyze();
            else goHome();
          }}
        />
      )}
      <MainContainer hasFooter={isDone}>
        <BackContainer>
          <BackIconWrapper href="/" onClick={handleBackClick}>
            <BackIcon />
          </BackIconWrapper>
          <TitleBlock>
            <PageTitleWrapper>
              <PageTitle>{translations(messages.pageTitle)}</PageTitle>
              <NewBadge>{translations(messages.new)}</NewBadge>
            </PageTitleWrapper>
            <PageSubtitle>{translations(componentMessages.pageSubtitle)}</PageSubtitle>
          </TitleBlock>
          <Toast visible={toastVisible}>
            <ToastIcon />
            {toastMessage}
          </Toast>
        </BackContainer>
        <Card>
          {isAnalyzing && <AnalyzingState progress={progress} />}
          {!isAnalyzing && !isDone && (
            <>
              <QuickOrderTextArea
                text={text}
                images={images}
                errorMessage={fileErrorMessage}
                onTextChange={setText}
                onFilesAdded={addFiles}
                onImageRemove={removeImage}
              />
              <AnalyzeButton hasContent={hasContent} onClick={handleAnalyze}>
                {translations(messages.analyzeButton)}
              </AnalyzeButton>
            </>
          )}
          {isDone && (
            <ResultLayout>
              <ResultBanner
                found={found}
                total={products.length}
                hasHigh={hasHigh}
                onSelectAllHigh={handleSelectAllHigh}
              />
              <ResultTable
                products={products}
                quantities={quantities}
                allSelected={allSelected}
                someSelected={someSelected}
                selectedCount={selectedCount}
                onToggleAll={toggleAll}
                onToggleRow={toggleRow}
                onQuantityChange={setQuantity}
                onChangeProduct={handleChangeProduct}
                onSwapProduct={handleSwapProduct}
                loggedIn={loggedIn}
                goToSignIn={goToSignIn}
              />
            </ResultLayout>
          )}
        </Card>
        {isDone && (
          <ResultFooter
            totalAmount={totalAmount}
            selectedCount={selectedCount}
            onReanalyze={handleReanalyze}
            onReanalyzeClick={hasNonNoneProduct ? () => setLeaveConfirmAction('reanalyze') : handleReanalyze}
            onAddToCart={handleAddToCart}
          />
        )}
      </MainContainer>
    </>
  );
};

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
  goToSignIn: () => dispatch(push('/signin')),
  goHome: () => dispatch(push('/')),
  gotoSignIn: () => dispatch(push('/signin')),
  onRefreshCart: () => dispatch(refreshCart()),
});

export default connect(null, mapDispatchToProps)(QuickOrder);
